import React, { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Badge, PageHeader } from "@/admin/ResourceTable";
import { RefreshCw, IndianRupee } from "lucide-react";

const tone = (s) => ({ successful: "green", failed: "red", pending: "amber", initiated: "gray", reversed: "amber" }[s] || "gray");

export default function UPIQueue() {
  const [data, setData] = useState({ items: [] });
  const [filter, setFilter] = useState("");
  const load = () => api.get("/upi-redemptions", { params: filter ? { status: filter, page_size: 50 } : { page_size: 50 } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [filter]);
  const retry = async (id) => { try { const { data } = await api.post(`/upi-redemptions/${id}/retry`); toast.success(`Retry → ${data.status}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const stats = { successful: 0, failed: 0, pending: 0 };
  data.items.forEach((r) => { stats[r.status] = (stats[r.status] || 0) + 1; });

  return (
    <div>
      <PageHeader title="UPI Redemptions" subtitle="Payout queue — failures never lose or duplicate points; retry from here">
        <button data-testid="upi-refresh" onClick={load} className="flex items-center gap-2 text-sm font-semibold border border-gray-300 px-3 py-2 rounded-lg hover:border-[#8B0000]"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </PageHeader>
      <div className="flex gap-2 mb-4">
        {[["", "All"], ["successful", "Successful"], ["failed", "Failed"], ["pending", "Pending"]].map(([v, l]) => (
          <button key={v} data-testid={`upi-filter-${v || "all"}`} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${filter === v ? "bg-[#8B0000] text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{l}</button>
        ))}
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead><tr className="bg-gray-50 border-b">{["User", "Points", "Amount", "UPI (masked)", "Holder", "Status", "Provider Ref", "Action"].map((h) => <th key={h} className="py-3 px-4 text-[11px] uppercase tracking-wider text-gray-600 font-bold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>{data.items.map((r) => (
            <tr key={r.id} className="border-b border-gray-100" data-testid={`upi-row-${r.id}`}>
              <td className="py-3 px-4 font-mono text-xs">{r.user_id?.slice(0, 8)}</td>
              <td className="py-3 px-4 font-bold">{r.points}</td>
              <td className="py-3 px-4">₹{r.amount_inr}</td>
              <td className="py-3 px-4 font-mono text-xs">{r.upi_masked}</td>
              <td className="py-3 px-4">{r.holder}</td>
              <td className="py-3 px-4"><Badge tone={tone(r.status)}>{r.status}</Badge>{r.failure_reason && <div className="text-[11px] text-red-500 mt-0.5">{r.failure_reason}</div>}</td>
              <td className="py-3 px-4 font-mono text-xs">{r.provider_reference || "—"}</td>
              <td className="py-3 px-4">{r.status === "failed" && <button data-testid={`upi-retry-${r.id}`} onClick={() => retry(r.id)} className="text-xs font-semibold text-[#8B0000] hover:underline">Retry</button>}</td>
            </tr>))}
            {data.items.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-gray-400">No UPI redemptions</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
